<script setup>
import { useI18n } from 'vue-i18n'
import DriverPortalLayout from '@/Layouts/DriverPortalLayout.vue'
import DeliveryListItem from '@/Components/DriverPortal/DeliveryListItem.vue'
import EarningsCard from '@/Components/DriverPortal/EarningsCard.vue'
import { Link, router } from '@inertiajs/vue3'

defineOptions({ layout: DriverPortalLayout })
const { t } = useI18n()

defineProps({
  active: Object,
  assigned: { type: Array, default: () => [] },
  broadcast: { type: Array, default: () => [] },
  earnings: {
    type: Object,
    default: () => ({ unpaid_cents: 0, paid_today_cents: 0, paid_week_cents: 0, unpaid_count: 0 })
  }
})

function claim(id) {
  if (!confirm('Claim this delivery?')) return
  router.patch(`/deliveries/${id}/claim`)
}

function startTrip(id) {
  router.patch(`/deliveries/${id}/start`)
}

function markDelivered(id) {
  if (!confirm('Mark this delivery as complete?')) return
  router.patch(`/deliveries/${id}/complete`)
}
</script>

<template>
  <h1 class="title">{{ t('driver_portal.dashboard.title') }}</h1>

  <EarningsCard :earnings="earnings" />

  <section v-if="active" class="active-card">
    <p class="active-card__kind">{{ active.kind === 'handover' ? 'Handover' : 'Pickup' }} <span class="active-card__pill">In progress</span></p>
    <h2 class="active-card__customer">{{ active.customer_name }}</h2>
    <a :href="`tel:${active.customer_phone}`" class="active-card__phone">📞 {{ active.customer_phone }}</a>
    <a :href="`https://maps.google.com/?q=${active.lat},${active.lng}`" target="_blank" rel="noopener" class="active-card__addr">📍 {{ active.address }}</a>
    <p class="active-card__time">{{ active.scheduled_for }}</p>
    <p v-if="active.notes" class="active-card__notes">{{ active.notes }}</p>
    <p class="active-card__vehicle">{{ active.vehicle.make }} {{ active.vehicle.model }} · {{ active.vehicle.registration }}</p>

    <button class="action action--primary" @click="markDelivered(active.id)">{{ t('driver_portal.dashboard.mark_delivered') }}</button>
    <Link :href="`/deliveries/${active.id}`" class="action action--secondary">{{ t('driver_portal.dashboard.details') }}</Link>
  </section>

  <section v-if="assigned.length" class="section">
    <h2 class="section__title">{{ t('driver_portal.dashboard.assigned') }}</h2>
    <DeliveryListItem v-for="d in assigned" :key="d.id" :delivery="d" />
  </section>

  <section v-if="broadcast.length" class="section">
    <h2 class="section__title">{{ t('driver_portal.dashboard.broadcast') }}</h2>
    <DeliveryListItem v-for="d in broadcast" :key="d.id" :delivery="d" show-claim />
  </section>

  <div v-if="!active && assigned.length === 0 && broadcast.length === 0" class="empty">
    {{ t('driver_portal.dashboard.empty') }}
  </div>
</template>

<style scoped>
.title { font-size: 1.5rem; font-weight: 700; margin: 0 0 1rem; color: var(--ink); }
.active-card {
  background: var(--surface); border: 1px solid var(--accent);
  border-radius: 0.75rem; padding: 1.25rem; margin-bottom: 1.5rem;
}
.active-card__kind { margin: 0 0 0.25rem; font-size: 0.8125rem; color: var(--muted); }
.active-card__pill { display: inline-block; padding: 0.1rem 0.4rem; background: var(--accent); color: var(--accentInk); border-radius: 999px; font-size: 0.6875rem; margin-left: 0.25rem; }
.active-card__customer { margin: 0 0 0.5rem; font-size: 1.25rem; }
.active-card__phone, .active-card__addr {
  display: block; padding: 0.6rem 0.85rem; background: var(--surfaceAlt);
  border-radius: 0.5rem; margin-bottom: 0.4rem; color: var(--ink); text-decoration: none;
  font-size: 0.9375rem;
}
.active-card__time { margin: 0.5rem 0; color: var(--inkSoft); font-size: 0.875rem; }
.active-card__notes { margin: 0.5rem 0; padding: 0.5rem 0.75rem; background: var(--surfaceAlt); border-left: 3px solid var(--accent); border-radius: 0.25rem; font-size: 0.875rem; }
.active-card__vehicle { margin: 0.5rem 0 1rem; color: var(--muted); font-size: 0.875rem; }
.action {
  display: block; width: 100%; padding: 0.75rem 1rem;
  border: 0; border-radius: 0.5rem; font-weight: 600; text-align: center;
  text-decoration: none; cursor: pointer; margin-bottom: 0.5rem; min-height: 2.75rem;
}
.action--primary { background: var(--accent); color: var(--accentInk); }
.action--secondary { background: transparent; color: var(--ink); border: 1px solid var(--line); }
.section { margin-bottom: 1.5rem; }
.section__title { font-size: 0.8125rem; text-transform: uppercase; color: var(--muted); margin: 0 0 0.5rem; letter-spacing: 0.05em; }
.empty { padding: 3rem 1rem; text-align: center; color: var(--muted); }
</style>
